import { useCallback, useEffect, useState, RefObject } from 'react';
import { EditorSelection } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { LogLocation, TreeIndexMap } from '../types';
import { useUI, useEditorState } from '../contexts';

type UseNavigationParams = {
  treeIndex: TreeIndexMap;
  editorViewRef: RefObject<EditorView | null>;
};

export function useNavigation({
  treeIndex,
  editorViewRef,
}: UseNavigationParams) {
  // Get UI state from context
  const { setStatus } = useUI();

  // Get editor state from context
  const { activeFile, setActiveFile, setOpenTabs } = useEditorState();
  const [pendingLocation, setPendingLocation] = useState<LogLocation | null>(null);

  const goToEditorLocation = useCallback(
    (fileId: string, line: number, column: number) => {
      if (!editorViewRef.current) return;
      if (activeFile !== fileId) return;
      const view = editorViewRef.current;
      const lineInfo = view.state.doc.line(Math.max(1, line));
      const offset = lineInfo.from + Math.max(column - 1, 0);
      view.dispatch({
        selection: EditorSelection.cursor(offset),
        scrollIntoView: true,
      });
    },
    [activeFile, editorViewRef]
  );

  const handleNavigateToLocation = useCallback(
    (loc: LogLocation) => {
      const normalize = (p: string) => p.replace(/^\.?[\\/]/, '').replace(/\\/g, '/');
      const candidates: string[] = [];
      const base = normalize(loc.path);
      candidates.push(base);
      if (base.includes('/src/')) candidates.push(base.slice(base.indexOf('src/')));
      if (base.includes('/public/')) candidates.push(base.slice(base.indexOf('public/')));
      const node =
        candidates.map((p) => treeIndex.byPath[p]).find(Boolean) ||
        candidates
          .map((p) => p.replace(/^\/+/, ''))
          .map((p) => treeIndex.byPath[p])
          .find(Boolean);
      if (!node) {
        setStatus(`Konum bulunamadı · ${loc.path}`);
        return;
      }
      setActiveFile(node.id);
      setOpenTabs((prev) => (prev.includes(node.id) ? prev : [...prev, node.id]));
      setPendingLocation({ fileId: node.id, path: node.path, line: loc.line, column: loc.column });
      setStatus(`Konuma gidiliyor · ${node.path}:${loc.line}:${loc.column}`);
    },
    [treeIndex, setActiveFile, setOpenTabs, setStatus]
  );

  // Handle pending location navigation
  useEffect(() => {
    if (!pendingLocation) return;
    const targetFileId = pendingLocation.fileId;
    if (!targetFileId || targetFileId !== activeFile) return;
    const timer = window.setTimeout(() => {
      goToEditorLocation(targetFileId, pendingLocation.line, pendingLocation.column);
      setPendingLocation(null);
    }, 10);
    return () => window.clearTimeout(timer);
  }, [activeFile, goToEditorLocation, pendingLocation]);

  return {
    pendingLocation,
    setPendingLocation,
    goToEditorLocation,
    handleNavigateToLocation,
  };
}
